export type ParticleType = 'spark' | 'star' | 'glitch' | 'ring';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  alpha: number;
  decay: number;
  type: ParticleType;
  rotation?: number;
  vr?: number;
}

export interface FloatingText {
  x: number;
  y: number;
  text: string;
  color: string;
  alpha: number;
  scale: number;
  targetScale: number;
  vy: number;
  shadowColor: string;
}

export class FXSystem {
  private particles: Particle[] = [];
  private texts: FloatingText[] = [];
  private shakeIntensity: number = 0;
  private shakeDecay: number = 0.88;
  public shakeX: number = 0;
  public shakeY: number = 0;

  // Screen-level hit flash
  public flashColor: string = '';
  public flashAlpha: number = 0;

  public triggerHit(
    x: number,
    y: number,
    quality: 'PERFECT' | 'GREAT' | 'GOOD' | 'MISS',
    combo: number,
    scoreGained: number
  ): void {
    let count = 16;
    let mainColor = '#a855f7';
    let label = '';
    let scale = 1.2;

    if (quality === 'PERFECT') {
      count = 28;
      const isOverdrive = combo >= 10;
      mainColor = isOverdrive ? '#fbbf24' : '#38bdf8';
      this.shake(5.5);
      this.flash(isOverdrive ? 'rgba(251, 191, 36, 0.22)' : 'rgba(56, 189, 248, 0.18)');

      const comboTag = combo >= 5 ? ` 🔥 x${Math.min(5, Math.floor(combo / 5) + 1)}` : '';
      label = `PERFECT! +${scoreGained}${comboTag}`;
      scale = 1.4;

      // Add a shockwave ring particle
      this.particles.push({
        x,
        y,
        vx: 0,
        vy: 0,
        size: 8,
        color: mainColor,
        alpha: 0.9,
        decay: 0.04,
        type: 'ring',
      });
    } else if (quality === 'GREAT') {
      count = 14;
      mainColor = '#34d399'; // Emerald
      this.shake(2.5);
      this.flash('rgba(52, 211, 153, 0.12)');
      label = `GREAT! +${scoreGained}`;
      scale = 1.15;
    } else if (quality === 'GOOD') {
      count = 8;
      mainColor = '#94a3b8'; // Slate
      this.shake(1.0);
      label = `GOOD +${scoreGained}`;
      scale = 0.95;
    } else {
      // MISS
      count = 20;
      mainColor = '#f43f5e'; // Rose red
      this.shake(9.0);
      this.flash('rgba(244, 63, 94, 0.25)');
      label = 'MISS! 💀';
      scale = 1.35;
    }

    this.addText(x, y - 24, label, mainColor, scale, mainColor);

    // Spawn burst particles
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = quality === 'PERFECT' ? Math.random() * 8 + 3 : Math.random() * 5 + 1.5;
      const type: ParticleType = quality === 'MISS' ? 'glitch' : (quality === 'PERFECT' && Math.random() > 0.4 ? 'star' : 'spark');

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - (quality === 'PERFECT' ? 1.5 : 0),
        size: quality === 'PERFECT' ? Math.random() * 5 + 2.5 : Math.random() * 3.5 + 1.5,
        color: mainColor,
        alpha: 1,
        decay: Math.random() * 0.035 + 0.02,
        type,
        rotation: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 6,
      });
    }
  }

  public triggerMilestone(x: number, y: number, combo: number): void {
    let title = `${combo}x COMBO!`;
    let color = '#38bdf8';

    if (combo >= 20) {
      title = `👑 20x MYTHIC AURA! 👑`;
      color = '#fbbf24';
      this.shake(8);
      this.flash('rgba(251, 191, 36, 0.35)');
    } else if (combo >= 15) {
      title = `⚡ 15x AURA DEMON! ⚡`;
      color = '#c084fc';
      this.shake(6);
      this.flash('rgba(192, 132, 252, 0.28)');
    } else if (combo >= 10) {
      title = `🔥 10x OVERDRIVE! 🔥`;
      color = '#fbbf24';
      this.shake(5);
      this.flash('rgba(251, 191, 36, 0.22)');
    } else if (combo >= 5) {
      title = `⚡ 5x LOCKED IN! ⚡`;
      color = '#38bdf8';
      this.shake(3.5);
      this.flash('rgba(56, 189, 248, 0.2)');
    }

    this.addText(x, y - 50, title, color, 1.5, color);

    // Milestone burst ring
    for (let i = 0; i < 24; i++) {
      const angle = (i / 24) * Math.PI * 2;
      const speed = Math.random() * 4 + 4;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: Math.random() * 4 + 2,
        color,
        alpha: 1,
        decay: 0.025,
        type: 'star',
      });
    }
  }

  public flash(color: string): void {
    this.flashColor = color;
    this.flashAlpha = 1.0;
  }

  public shake(amount: number): void {
    this.shakeIntensity = Math.max(this.shakeIntensity, amount);
  }

  public addText(
    x: number,
    y: number,
    text: string,
    color: string,
    scale: number = 1.0,
    shadowColor: string = '#000000'
  ): void {
    this.texts.push({
      x,
      y,
      text,
      color,
      alpha: 1,
      scale: scale * 0.7,
      targetScale: scale,
      vy: -1.8,
      shadowColor,
    });
  }

  public update(dt: number): void {
    // Screen shake update
    if (this.shakeIntensity > 0.1) {
      this.shakeX = (Math.random() * 2 - 1) * this.shakeIntensity;
      this.shakeY = (Math.random() * 2 - 1) * this.shakeIntensity;
      this.shakeIntensity *= Math.pow(this.shakeDecay, dt * 60);
    } else {
      this.shakeX = 0;
      this.shakeY = 0;
      this.shakeIntensity = 0;
    }

    // Flash decay
    if (this.flashAlpha > 0.01) {
      this.flashAlpha *= Math.pow(0.85, dt * 60);
    } else {
      this.flashAlpha = 0;
    }

    // Particles update
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx;
      p.y += p.vy;

      if (p.type === 'ring') {
        p.size += 90 * dt;
      } else {
        p.vx *= 0.94;
        p.vy *= 0.94;
        if (p.vr) {
          p.rotation = (p.rotation || 0) + p.vr * dt;
        }
      }

      p.alpha -= p.decay;
      if (p.alpha <= 0) {
        this.particles.splice(i, 1);
      }
    }

    // Texts update
    for (let i = this.texts.length - 1; i >= 0; i--) {
      const t = this.texts[i];
      t.y += t.vy;
      // Spring to target scale
      t.scale += (t.targetScale - t.scale) * 0.2;
      t.alpha -= 0.022;
      if (t.alpha <= 0) {
        this.texts.splice(i, 1);
      }
    }
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    // Render screen hit flash
    if (this.flashAlpha > 0.01 && this.flashColor) {
      ctx.save();
      ctx.globalAlpha = this.flashAlpha;
      ctx.fillStyle = this.flashColor;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    }

    // Render particles
    for (const p of this.particles) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, p.alpha);

      if (p.type === 'ring') {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 2.5;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(1, p.size), 0, Math.PI * 2);
        ctx.stroke();
      } else if (p.type === 'glitch') {
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 6;
        ctx.translate(p.x, p.y);
        if (p.rotation) ctx.rotate(p.rotation);
        ctx.fillRect(-p.size, -p.size * 0.4, p.size * 2, p.size * 0.8);
      } else if (p.type === 'star') {
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8;
        ctx.translate(p.x, p.y);
        if (p.rotation) ctx.rotate(p.rotation);
        // Draw 4-point diamond star
        ctx.beginPath();
        ctx.moveTo(0, -p.size);
        ctx.lineTo(p.size * 0.35, 0);
        ctx.lineTo(0, p.size);
        ctx.lineTo(-p.size * 0.35, 0);
        ctx.closePath();
        ctx.fill();
      } else {
        // Spark circle
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(0.5, p.size), 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }

    // Render floating feedback texts
    for (const t of this.texts) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, t.alpha);
      ctx.fillStyle = t.color;
      const fontSize = Math.round(18 * t.scale);
      ctx.font = `900 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = t.shadowColor;
      ctx.shadowBlur = 12;
      ctx.fillText(t.text, t.x, t.y);
      ctx.restore();
    }
  }

  public clear(): void {
    this.particles = [];
    this.texts = [];
    this.shakeIntensity = 0;
    this.shakeX = 0;
    this.shakeY = 0;
    this.flashAlpha = 0;
  }
}
